import {
  AccountCircleOutlined as AccountCircleIcon,
  ContentCopyRounded as ContentCopyRoundedIcon,
  EmailOutlined as EmailOutlinedIcon,
} from '@mui/icons-material';
import { Avatar, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useCallback, useMemo } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { ColActions } from '@/pages/ValuationsV2/components/Col';
import { useLeadAnalyst } from '../../../hooks/useLeadAnalyst';
import { useValuationsStore } from '../../../store/valuationsStore';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: 'inherit',
    placeContent: 'start',
  },
  avatar: (theme: Theme) => ({
    color: 'inherit',
    backgroundColor: 'color-mix(in srgb, currentColor 15%, transparent)',
    width: 'clamp(2rem, 10vw, 4.2rem)',
    height: 'auto',
    aspectRatio: '1/1',
    '& svg': {
      fontSize: 'clamp(2rem, 6vw, 4rem)',
      color: theme.palette.primary.main,
    },
  }),
  analystName: (theme: Theme) => ({
    fontWeight: 600,
    fontSize: theme.fontSizes.base,
    color: 'inherit',
  }),
  email: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller,
    color: theme.palette.text.secondary,
  }),
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    placeItems: 'center',
    margin: 'auto',
  },
  helperText: (theme: Theme) => ({
    color: 'inherit',
    fontSize: theme.fontSizes.smaller,
    lineHeight: 1.4,
  }),
  actionButton: {
    width: '100%',
  },
  iconButton: {
    width: '100%',
    minWidth: '3.5rem',
  },
};

type ErrorStateProps = {
  message: string;
};

const ErrorState = ({ message }: ErrorStateProps) => (
  <Typography variant='body2' color='error'>
    {message}
  </Typography>
);

type EmptyStateProps = {
  message: string;
};

const EmptyState = ({ message }: EmptyStateProps) => (
  <Typography variant='body2' sx={styles.helperText}>
    {message}
  </Typography>
);

type LeadAnalystData = {
  name: string;
  email: string;
};

type LeadAnalystContentProps = {
  analyst: LeadAnalystData;
  onCopyEmail: () => void;
};

const LeadAnalystContent = ({ analyst, onCopyEmail }: LeadAnalystContentProps) => {
  const mailtoHref = useMemo(() => `mailto:${analyst.email}`, [analyst.email]);

  return (
    <Box sx={styles.container} data-id='lead-analyst-card'>
      <Box component='header' sx={styles.header}>
        <Avatar sx={styles.avatar}>
          <AccountCircleIcon fontSize='large' />
        </Avatar>
        <Typography component='span' sx={styles.analystName}>
          {analyst.name}
        </Typography>
        <Typography component='span' variant='caption' sx={styles.email}>
          {analyst.email}
        </Typography>
      </Box>
      <ColActions
        variant='highlighted'
        sx={{
          gridTemplateColumns: 'auto 1fr',
        }}
      >
        <Tooltip title='Copy email address'>
          <IconButton
            aria-label='Copy email address'
            color='primary'
            onClick={onCopyEmail}
            data-id='lead-analyst-copy-email-button'
            sx={styles.iconButton}
          >
            <ContentCopyRoundedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Email lead analyst'>
          <Button
            variant='outlined'
            sx={styles.actionButton}
            size='small'
            startIcon={<EmailOutlinedIcon fontSize='small' />}
            href={mailtoHref}
            data-id='lead-analyst-contact-button'
          >
            Email
          </Button>
        </Tooltip>
      </ColActions>
    </Box>
  );
};

const LeadAnalystView = () => {
  const selectedAssetDetails = useValuationsStore.use.selectedAssetDetails();
  const snackbarContext = useSnackBarContext();
  const { copy } = useCopyToClipboard();

  const countryIsoCode = useMemo(
    () => selectedAssetDetails?.country?.isoCode ?? null,
    [selectedAssetDetails?.country?.isoCode],
  );

  const { leadAnalyst, loading, error } = useLeadAnalyst(countryIsoCode);

  const handleCopyEmail = useCallback(async (): Promise<void> => {
    if (!leadAnalyst?.email) {
      return;
    }

    const success = await copy(leadAnalyst.email);

    if (!snackbarContext?.setSnack) {
      return;
    }

    if (success) {
      snackbarContext.setSnack({
        open: true,
        severity: 'success',
        message: 'Email copied to clipboard.',
      });
    } else {
      snackbarContext.setSnack({
        open: true,
        severity: 'error',
        message: 'Unable to copy email. Please try again.',
      });
    }
  }, [leadAnalyst?.email, copy, snackbarContext]);

  if (!selectedAssetDetails) {
    return null;
  }

  if (loading) {
    return null;
  }

  if (error) {
    return <ErrorState message='We were unable to load the lead analyst details right now.' />;
  }

  if (!leadAnalyst) {
    return <EmptyState message='Lead analyst information is not available for this asset.' />;
  }

  return <LeadAnalystContent analyst={leadAnalyst} onCopyEmail={handleCopyEmail} />;
};

export { LeadAnalystView };
